// Plinto case-study page behaviour: the data-driven tab sections.
// Mirrors the Repper page pattern (src/js/repper.js).
//
// Images/media are imported (not referenced as string paths) so the bundler
// fingerprints them and emits them to the build output.

// ── PLACEHOLDER media ──────────────────────────────────────────────────────
// Real Plinto assets aren't in yet, so we reuse existing Repper images. When
// the real assets land, drop them in src/assets/images/plinto/ and swap these
// imports + the `media.src` references below.
import placeholderB from 'url:../assets/images/plinto/plinto_budget_review_after_overview.png';
import placeholderC from 'url:../assets/images/plinto/plinto_budget_review_after_sidebar.png';
import placeholderA from 'url:../assets/images/plinto/plinto_budget_review_before.png';
import placeholderF from 'url:../assets/images/plinto/solution_action.png';
import placeholderD from 'url:../assets/images/plinto/solution_period_picker.png';

// Second-iteration refactor screens.
import refactorAlignment from 'url:../assets/images/plinto/plinto_refactor_alignment.png';
import refactorControls from 'url:../assets/images/plinto/plinto_refactor_controls.png';
import refactorOverview from 'url:../assets/images/plinto/plinto_refactor_overview.png';
import refactorSidebar from 'url:../assets/images/plinto/plinto_refactor_sidebar.png';
import refactorTable from 'url:../assets/images/plinto/plinto_refactor_table.png';

// Hero assets. These were `src="./…"` in plinto.html before the content moved
// into JS (so the page is empty until the gate unlocks); imported via `url:`
// so Parcel still fingerprints them.
import logoUrl from 'url:../assets/images/plinto/plinto_logo.png';
import heroVideoUrl from 'url:../assets/images/plinto/plinto_video.mp4';
import heroVideoPosterUrl from 'url:../assets/images/plinto/plinto_video_placeholder.png';

/* ── Tab sections ──────────────────────────────────────────────────────────
   To edit copy or swap media, only this data structure needs to change.
   media.type is 'image' or 'video'. `eyebrow` is an optional small title that
   renders above the section heading. */
const sections = [
  {
    mount: 'design-foundation',
    heading: 'Creating a design foundation',
    // Index of the tab shown when the section first renders (defaults to 0).
    defaultTab: 1,
    // `intro` can be a plain string or an array of blocks (paragraphs and
    // { subtitle } small titles) for richer left-column copy.
    intro: [
      'The UI had grown screen by screen, so my first goal was to create unity in layout and visual design. There were already tables and lists, central to financial controlling; now came a layer of AI on top. I reduced the chrome, relying mostly spacing and lines to segment the UI. This created space for the new AI reviews to stand out.',
      { subtitle: 'AI in budget review' },
      'The Budget review is where financial controllers get both an overview of an asset as well as hunt for exceptions that need attention. To meet both needs, I layered the AI review: summary for the overall verdict, issues per account bubbling up in the tree structure, and all details in the sidebar.',
    ],
    tabs: [
      {
        label: 'Before',
        media: {
          type: 'image',
          src: placeholderA,
          width: 800,
          height: 517,
          alt: 'Plinto style foundations (placeholder)',
        },
      },
      {
        label: 'After (overview)',
        media: {
          type: 'image',
          src: placeholderB,
          width: 800,
          height: 517,
          alt: 'Plinto components (placeholder)',
        },
      },
      {
        label: 'After (sidebar)',
        media: {
          type: 'image',
          src: placeholderC,
          width: 800,
          height: 517,
          alt: 'Plinto review examples (placeholder)',
        },
      },
    ],
  },
  {
    mount: 'custom-solutions',
    heading: 'Solutions meeting context ',
    intro:
      "Plinto's users live in complex systems like ERP. We didn't believe that software in this domain needs to be clunky or hard to navigate. At the same time, our users value utility and efficiency, and so where fit, I created custom solutions to better fit specific use cases. This balancing act meant often repurposing well-known UI patterns in new ways.",
    tabs: [
      {
        label: 'Period picker',
        body: 'The original period picker was simply two date pickers. Since months, quarters and years are the most common periods used by the users, I brought those to the foreground and structured them so they were each to pick. A second QoL improvement: any custom date range would be presented as simple as possible, breaking in into year, quarter or month ranges where applicable (see right picker).',
        media: {
          type: 'image',
          src: placeholderD,
          width: 800,
          height: 517,
          alt: 'Plinto custom card (placeholder)',
        },
      },
      // {
      //   label: 'AI assistance',
      //   body: "Plinto revolves around controls, which are checks being run on accounts and invoices. We built an AI-assisted creation flow, which lets the user loosely explain what they're after and get a detailed control back.",
      //   media: {
      //     type: 'image',
      //     src: placeholderE,
      //     width: 800,
      //     height: 517,
      //     alt: 'Plinto form layout (placeholder)',
      //   },
      // },
      {
        label: 'Actions in context',
        body: "As we're moving from AI review to agentic actions, we need a consistent way to display them across the UI. This is an proposal of how that can work in cards, sidebars, and timelines.",
        media: {
          type: 'image',
          src: placeholderF,
          width: 800,
          height: 517,
          alt: 'Plinto calendar (placeholder)',
        },
      },
    ],
  },
  {
    mount: 'second-iteration',
    heading: 'Second visual iteration',
    intro:
      "Before the end of my contract, I did a second interation on the design system. The product had grown a lot in a short time and there was again a need for more breathing space and finetuning the UI's balance and contrast. Among dozens of tweaks, I straightened the layout, separated the navbar out, receded all interactive UI slightly to lift content to the foreground, an balanced out all text styles.",
    tabs: [
      {
        label: 'Sidebar',
        media: {
          type: 'image',
          src: refactorSidebar,
          width: 1496,
          height: 935,
          alt: 'Plinto second iteration — sidebar',
        },
      },
      {
        label: 'Alignment',
        media: {
          type: 'image',
          src: refactorAlignment,
          width: 1496,
          height: 935,
          alt: 'Plinto second iteration — alignment',
        },
      },
      {
        label: 'Table',
        media: {
          type: 'image',
          src: refactorTable,
          width: 1496,
          height: 935,
          alt: 'Plinto second iteration — table',
        },
      },
      {
        label: 'Overview',
        media: {
          type: 'image',
          src: refactorOverview,
          width: 1496,
          height: 935,
          alt: 'Plinto second iteration — overview',
        },
      },
      {
        label: 'Controls',
        media: {
          type: 'image',
          src: refactorControls,
          width: 1496,
          height: 935,
          alt: 'Plinto second iteration — controls',
        },
      },
    ],
  },
];

// width/height attributes let the browser reserve the aspect-ratio box before
// the image loads, so the panel doesn't jump in height once it arrives.
const dims = (media) =>
  media.width && media.height
    ? ` width="${media.width}" height="${media.height}"`
    : '';

const mediaMarkup = (media) =>
  media.type === 'video'
    ? `<video controls playsinline poster="${media.poster ?? ''}" src="${media.src}"${dims(media)}></video>`
    : `<img src="${media.src}" alt="${media.alt ?? ''}"${dims(media)} />`;

// A text block is either a paragraph (string) or a small title ({ subtitle }).
const blockMarkup = (block) =>
  typeof block === 'string'
    ? `<p>${block}</p>`
    : block.subtitle
      ? `<h3 class="reflection-subtitle">${block.subtitle}</h3>`
      : '';

// An optional title shows inline, in the default text color, before an em dash.
// Returns '' when neither title nor body is provided.
const bodyMarkup = (tab) => {
  const title = tab.title
    ? `<span class="reflection-panel__title">${tab.title}</span>`
    : '';
  if (title && tab.body) return `${title} — ${tab.body}`;
  return title || tab.body || '';
};

const renderPanel = (panel, tab) => {
  const body = bodyMarkup(tab);
  panel.innerHTML = `
    ${body ? `<div class="reflection-panel__body">${body}</div>` : ''}
    <div class="reflection-panel__media">${mediaMarkup(tab.media)}</div>`;
};

const initializeVideoFacades = (root = document) => {
  root.querySelectorAll('.video-facade[data-video-src]').forEach((facade) => {
    facade.addEventListener(
      'click',
      () => {
        const video = document.createElement('video');
        video.src = facade.dataset.videoSrc;
        video.poster = facade.dataset.videoPoster ?? '';
        video.controls = true;
        video.playsInline = true;
        video.autoplay = true;
        video.preload = 'metadata';
        facade.replaceWith(video);
        video.play().catch(() => {});
      },
      { once: true },
    );
  });
};

// The static hero / hero-media / role markup that used to live in plinto.html.
// Class names match the existing CSS, so styling applies unchanged.
const contentMarkup = () => `
  <section class="project-hero">
    <hgroup>
      <img class="icon" src="${logoUrl}" alt="Plinto icon" />
      <h1>Plinto</h1>
    </hgroup>
    <p class="project-description">
      An AI assistant that makes financial controlling comprehensive,
      accurate, and continuous. Controllers orchestrate while Plinto
      handles.
    </p>
    <dl class="fact-grid">
      <div class="fact-col">
        <dt>Type</dt><dd>AI Fintech</dd>
        <dt>Year</dt><dd>Sep 2025 - Jun 2026</dd>
      </div>
      <div class="fact-col">
        <dt>Role</dt><dd>Contractor</dd>
        <dt>Activities</dt><dd>Design system, product design, frontend</dd>
      </div>
    </dl>
  </section>

  <section class="project-section">
    <div class="media-embed">
      <button
        class="video-facade"
        type="button"
        data-video-src="${heroVideoUrl}"
        data-video-poster="${heroVideoPosterUrl}"
        aria-label="Play video: Plinto product walkthrough"
      >
        <img src="${heroVideoPosterUrl}" alt="" />
        <span class="video-facade__play" aria-hidden="true"></span>
      </button>
    </div>
  </section>

  <section class="project-section project-section--boxed">
    <p class="text-body text-balance">
      Plinto had a strong technical basis going towards a pre-seed round
      and wanted to elevate it to a real product feel. Without designer
      and with founders stretched thin, they
      needed someone who could take any task and run with it. I touched
      most parts of the product, from new features to the design foundation below, 
      within a budget of 2 days a week.
    </p>
  </section>

  ${sections
    .map(
      (section) =>
        `<section class="reflection-tabs" data-reflection="${section.mount}"></section>`,
    )
    .join('')}`;

// Renders the case-study content into <main>. Idempotent — a second call is a no-op.
let rendered = false;
export function renderContent() {
  if (rendered) return;
  const main = document.querySelector('main');
  if (!main) return;
  main.innerHTML = contentMarkup();
  rendered = true;

  initializeVideoFacades(main);
  buildSections();
}

renderContent();

function buildSections() {
  sections.forEach((section) => {
    const root = document.querySelector(
      `.reflection-tabs[data-reflection="${section.mount}"]`,
    );
    if (!root) return;

    const heading = document.createElement('h2');
    heading.className = 'reflection-tabs__heading';
    heading.textContent = section.heading;

    // An optional small title (eyebrow) sits above the heading. When present we
    // group it with the heading so the pair occupies the grid "heading" area.
    let headingCell = heading;
    if (section.eyebrow) {
      const eyebrow = document.createElement('p');
      eyebrow.className = 'reflection-tabs__eyebrow';
      eyebrow.textContent = section.eyebrow;

      headingCell = document.createElement('div');
      headingCell.className = 'reflection-tabs__heading-group';
      headingCell.append(eyebrow, heading);
    }

    // `intro` is either a plain string (single paragraph) or an array of blocks.
    let lead;
    if (Array.isArray(section.intro)) {
      lead = document.createElement('div');
      lead.className = 'reflection-tabs__lead';
      lead.innerHTML = section.intro.map(blockMarkup).join('');
    } else {
      lead = document.createElement('p');
      lead.className = 'reflection-tabs__lead';
      lead.textContent = section.intro;
    }

    const tabbar = document.createElement('div');
    tabbar.className = 'reflection-tabbar';
    tabbar.setAttribute('role', 'tablist');

    const panel = document.createElement('div');
    panel.className = 'reflection-panel';
    panel.setAttribute('role', 'tabpanel');

    const defaultTab = section.defaultTab ?? 0;

    const buttons = section.tabs.map((tab, i) => {
      const button = document.createElement('button');
      button.className = 'reflection-tab';
      button.type = 'button';
      button.textContent = tab.label;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', String(i === defaultTab));
      button.addEventListener('click', () => {
        buttons.forEach((b) => b.setAttribute('aria-selected', 'false'));
        button.setAttribute('aria-selected', 'true');
        renderPanel(panel, tab);
      });
      tabbar.appendChild(button);
      return button;
    });

    // Append in reading order (heading → lead → tabs → panel); grid areas place them.
    root.append(headingCell, lead, tabbar, panel);
    renderPanel(panel, section.tabs[defaultTab]);
  });
}
